module.exports={render:function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _vm._m(0)
},staticRenderFns: [function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _c('div', {
    staticClass: "chapter"
  }, [_c('div', {
    staticClass: "inner_page"
  }, [_c('div', {
    staticClass: "chapter",
    attrs: {
      "id": "prog"
    }
  }, [_c('h1', {
    staticClass: "ctfm"
  }, [_c('a', {
    staticClass: "page",
    attrs: {
      "id": "pxi"
    }
  }), _c('a', {
    staticClass: "xref",
    attrs: {
      "href": "#/eerpsalmschristian0005#rprog"
    }
  }, [_vm._v("Prologue")])]), _vm._v(" "), _c('p', {
    staticClass: "pf"
  }, [_vm._v("Biblical lament is too mysterious to equate cheaply with psychological complaint. Nor can it be comprehended exhaustively for a seminary textbook. It certainly reflects upon the human condition, but it also reflects upon the character of God. It is a vital aspect, then, of theological anthropology, itself an increasingly central concern for Christianity in the twenty-first century. Our study of lament psalms will hopefully provide a basis for a theology of lament.")]), _vm._v(" "), _c('p', [_vm._v("Our motive is not that of previous scholarship that identified one genre or category of the Psalter as “lament psalms,” in contrast to other genres, such as praise."), _c('a', {
    staticClass: "fnref",
    attrs: {
      "href": "#/eerpsalmschristian0006#fn1",
      "id": "fn1r"
    }
  }, [_vm._v("1")]), _vm._v(" Our selection of psalms would then be debatable, for other psalms could have been chosen as more expressive of the genre identified as “lament.” We have, in fact, in our collaborate effort to combine the history of the interpretation with contemporary exegesis of selected psalms, simply taken the traditional “seven penitential psalms,” of which Psalm 51 was already selected in our previous work,"), _c('a', {
    staticClass: "fnref",
    attrs: {
      "href": "#/eerpsalmschristian0006#fn2",
      "id": "fn2r"
    }
  }, [_vm._v("2")]), _vm._v(" together with Psalms 5 to 7 as a cluster, together with special pleas for Psalms 44 and 49.")]), _vm._v(" "), _c('p', [_vm._v("As we shall see, the early Church Fathers did not take their “penitential” character with the same literal emphasis as the medieval culture was to do later. Our sample, then, is in no sense comprehensive, but more contextual of a basic human posture of our finitude, of our sinful nature, of our need of redemption, of our trust and communion with God, all in the light of God’s purpose for humanity to be created and destined in the "), _c('i', [_vm._v("imago dei.")])]), _vm._v(" "), _c('p', [_vm._v("As for finitude, the problem of being persecuted for righteousness’ sake was more vexing for the psalmist in the old dispensation than for Christians in the new dispensation. The old dispensation promised blessings to those who "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "pxii"
    }
  }), _vm._v("were faithful and obedient to God’s law and threatened his punishment against the unfaithful and disobedient (cf. Leviticus 26; Deuteronomy 28). Though the first prophecy in the Bible — “he [the offspring of the woman] will crush your [the Serpent’s] head and you will strike his heel” (Gen. 3:15) — hints at the persecution of the righteous, the Book of the Law by Moses, the human founder of Israel, did not express that inevitability. In the old dispensation, many saints (e.g., Abel, Job, Moses, Jeremiah) suffered, like the psalmists, for being faithful. Several Old Testament stories recognize the spiritually formative value of suffering. In the wilderness, Israel learned what living with the "), _c('span', {
    staticClass: "tetr"
  }, [_vm._v("LORD")]), _vm._v(" meant. Through causing Israel to hunger and then feeding them, the "), _c('span', {
    staticClass: "tetr"
  }, [_vm._v("LORD")]), _vm._v(" taught them to be teachable (Exod. 16:4; Deut. 8:2-4). By allowing the Canaanites to remain in the land he taught holy warfare to the descendants of Joshua’s generation (Judg. 3:1-2). But mostly the old dispensation kept saints in the dark about the necessity of the righteous to suffer the buffeting of the wicked. By contrast, the Lord Jesus Christ, confiding in his followers as friends, teaches them clearly that they will be persecuted. “Servants,” he said, “are not greater than their master. If they [the world] persecuted me, they will persecute you also” (John 15:20). Because of their finitude, Christians are still perplexed about undeserved sufferings (2 Cor. 4:8-9), but, because they have been forewarned, they do not protest them but expect them (cf. 2 Tim. 2:3, 12), unlike the innocent psalmists, who protest their sufferings."), _c('a', {
    staticClass: "fnref",
    attrs: {
      "href": "#/eerpsalmschristian0006#fn3",
      "id": "fn3r"
    }
  }, [_vm._v("3")]), _vm._v(" In short, as a result of Christ’s forewarning, one cannot speak of “the psalms as Christian complaint.”")]), _vm._v(" "), _c('p', [_vm._v("To be sure, Christians, like the psalmists, mourn their sufferings, and they hunger and thirst for righteousness (cf. Matt. 5:3-11). The Lord Jesus with the psalmist said “my soul is troubled” (John 12:27; Ps. 6:2-3[3-4]), and “into your hands I commit my spirit” (Luke 23:46; Ps. 31:5[6]). Like the lamenting psalmist, he was “hated without reason” (John 15:25; Ps. 35:19) and a “close friend lifted his heel against [him]” (John 13:18; Ps. 41:9[10]). Paul also identified with the psalmist when he wrote: “For your sake we face death all day long; we are considered as sheep to be slaughtered” (Rom. 8:36; Ps. 44:22[23]). But, unlike the psalmist, Christians rejoice in their suffering, and this for two reasons. First, Christians, more so than the psalmists, know that undeserved sufferings produce virtues (Rom. 5:3-5; Jas. 1:2-3; 1 Pet. 1:7). And second, because Jesus Christ “has brought life and immortality to light” through his death for sin, burial, and authenticated resurrection (2 Tim. 1:10; 1 Cor. 15:3-8), they know better than the psalmist that great is the reward in heaven of those who are persecuted because of righteousness and faith in Jesus Christ (Matt. 5:10-12; 1 Pet. 4:13). Francis Bacon said well: “Prosperity is the blessing of the "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "pxiii"
    }
  }), _vm._v("Old Testament; adversity is the blessing of the New, which carries the greater benediction, and the clearer revelation of God’s favor.”"), _c('a', {
    staticClass: "fnref",
    attrs: {
      "href": "#/eerpsalmschristian0006#fn4",
      "id": "fn4r"
    }
  }, [_vm._v("4")]), _vm._v(" Moberly comments on this opposition and persecution: “The Christian vision can contextualize such things within the life of discipleship.”"), _c('a', {
    staticClass: "fnref",
    attrs: {
      "href": "#/eerpsalmschristian0006#fn5",
      "id": "fn5r"
    }
  }, [_vm._v("5")]), _vm._v(" In short, one cannot speak of “the psalms as Christian joy in suffering.”")]), _vm._v(" "), _c('p', [_vm._v("Being “poor” and being in “lament” are linked in the Psalter: in seeking righteousness in the law court as a plaintiff; in crying out for help in danger, oppression, and the threat of death; in need of health and cure in the presence of sickness and disease; and, in the truly penitential psalms, in seeking forgiveness, redemption, and restoration of communion with God."), _c('a', {
    staticClass: "fnref",
    attrs: {
      "href": "#/eerpsalmschristian0006#fn6",
      "id": "fn6r"
    }
  }, [_vm._v("6")]), _vm._v(" Lament is then both individual and national; and this is especially true in the psalms, for they are often the lament of Israel’s king, who is in corporate solidarity with his people."), _c('a', {
    staticClass: "fnref",
    attrs: {
      "href": "#/eerpsalmschristian0006#fn7",
      "id": "fn7r"
    }
  }, [_vm._v("7")])]), _vm._v(" "), _c('p', [_vm._v("Mysteriously, Jesus Christ himself, as the God-Man, fed his inner life in communion with his Father, at the significant stages of his life and death, on the Hebrew Psalms. He probably first learned them at his mother’s knee as a small child (cf. Ps. 22:9-10[10-11]; 2 Tim. 1:5; 3:14-15). When he was baptized in solidarity with all humanity, the recitation of a psalm gave clarity to his earthly mission. In his nakedness and cruel suffering on the cross, it was with a psalm that he died."), _c('a', {
    staticClass: "fnref",
    attrs: {
      "href": "#/eerpsalmschristian0006#fn8",
      "id": "fn8r"
    }
  }, [_vm._v("8")]), _vm._v(" As the epistle to the Hebrews comments, “in the days of his flesh, Jesus offered up prayers and supplications with loud cries and tears, to him who was able to save him from death, and he was heard because of his reverence” (Heb. 5:7). Likewise in the persecution and suffering of his followers, Paul and Silas, who chanted psalms at midnight while they were imprisoned (Acts 16:25).")]), _vm._v(" "), _c('p', [_vm._v("As the Fathers of the fourth century struggled to sustain both the humanity and the deity of Christ, within the Greek culture of the immutability of the divine, the Nicene Christianity that was shaped through these struggles inserted a critical article: “for us . . . he was made man.” This we may paraphrase as “who for the sake of human persons was made a human person.” The incarnation is "), _c('i', [_vm._v("for")]), _vm._v(" a specifiable objective."), _c('a', {
    staticClass: "fnref",
    attrs: {
      "href": "#/eerpsalmschristian0006#fn9",
      "id": "fn9r"
    }
  }, [_vm._v("9")]), _vm._v(" Biblical lament is subsumed within this divine "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "pxiv"
    }
  }), _vm._v("purpose. For to have a genuine human existence as God intended us to enjoy is to exercise lament before him. This is expressive of his sovereign grace, of our trust in his good purposes, and of our final destiny, to be transformed to the image of his Son.")]), _vm._v(" "), _c('p', [_vm._v("Our historical commentaries are not comprehensive; rather, they are selected vignettes showing how lament was exercised for particular concerns and personal issues at differing periods of church history. Each of the early Fathers has his own style of pastoral theology that expresses his own personhood. Only from the time of Bede and Alcuin do the numerical “seven Penitential psalms” begin to have social force, as the penitential culture from the thirteenth century until the Reformation dominated the use of the Psalter."), _c('a', {
    staticClass: "fnref",
    attrs: {
      "href": "#/eerpsalmschristian0006#fn10",
      "id": "fn10r"
    }
  }, [_vm._v("10")]), _vm._v(" As Michael P. Kuczynski observes so well: “The psalms came to shape late medieval moral discourse so dramatically because writers who knew the Psalms intimately . . . argued that the ethical principles latent in the Psalms could and must be applied to the daily everyday behavior of late medieval people.”"), _c('a', {
    staticClass: "fnref",
    attrs: {
      "href": "#/eerpsalmschristian0006#fn11",
      "id": "fn11r"
    }
  }, [_vm._v("11")])]), _vm._v(" "), _c('p', [_vm._v("In the England of Henry VIII, the penitential psalms might subtly have been used as a political protest against his marital affairs."), _c('a', {
    staticClass: "fnref",
    attrs: {
      "href": "#/eerpsalmschristian0006#fn12",
      "id": "fn12r"
    }
  }, [_vm._v("12")]), _vm._v(" Lament psalms were also appropriated in the ways rivalry operated even among the reformers.")]), _vm._v(" "), _c('p', [_vm._v("Such historical insights should caution our use and perhaps misuse of the lament psalms for our own cultural contexts and individual agendas. For as we face the aging of a large segment of the population, we are beginning to reinterpret the Baby Boomers’ culture of professional “success” into “a disability culture” of caring for an excessive population of the aged."), _c('a', {
    staticClass: "fnref",
    attrs: {
      "href": "#/eerpsalmschristian0006#fn13",
      "id": "fn13r"
    }
  }, [_vm._v("13")]), _vm._v(" Lament, then, will take on new significance.")]), _vm._v(" "), _c('p', [_vm._v("Such shifting perspectives make it all the more important that “lament” be based on its biblical expressions. Crucial in this century is the need of a deepening understanding of theological anthropology. Just as the mystery of "), _c('i', [_vm._v("creatio ex nihilo")]), _vm._v(" is linked with the call of Abraham, so too human personhood cannot be "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "pxv"
    }
  }), _vm._v("understood without the doctrine of the "), _c('i', [_vm._v("imago dei.")]), _c('a', {
    staticClass: "fnref",
    attrs: {
      "href": "#/eerpsalmschristian0006#fn14",
      "id": "fn14r"
    }
  }, [_vm._v("14")]), _vm._v(" Both are metaphysical categories that do not contradict human sciences, but add a dimension unavailable to human understanding. “Lament-before-God” is a similar category, transcending human complaint when only viewed in terms of secular psychology."), _c('a', {
    staticClass: "fnref",
    attrs: {
      "href": "#/eerpsalmschristian0006#fn15",
      "id": "fn15r"
    }
  }, [_vm._v("15")]), _vm._v(" No one scholarly discipline can be independent of the other, as we are trying to express in this work. For we are standing at a new door of perception, a new specialty that may become for the next generation of scholars “a theology of lament.”")]), _vm._v(" "), _c('p', [_vm._v("Our biblical exegete, Dr. Bruce Waltke, professor emeritus of biblical studies, Regent College, has devoted much of his academic life to the textual study of the Psalms, to give us, like Calvin before him, “the plain meaning of the text.”"), _c('a', {
    staticClass: "fnref",
    attrs: {
      "href": "#/eerpsalmschristian0006#fn16",
      "id": "fn16r"
    }
  }, [_vm._v("16")]), _vm._v(" His exegetical studies comprise the central substance of our book. Dr. James Houston, founding principal of Regent College and professor emeritus of spiritual theology, has provided the history of commentary and the personal profiles of its selected contributors. Dr. Erika Moore, professor of Old Testament at Trinity School for Ministry in Ambridge, Pennsylvania, wrote the exegetical portion for Psalm 39, did valuable editing, and prepared the glossary and indices."), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "pxvi"
    }
  })]), _vm._v(" "), _c('hr'), _vm._v(" "), _c('div', {
    staticClass: "notes"
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('a', {
    staticClass: "fnnum",
    attrs: {
      "href": "#/eerpsalmschristian0006#fn1r",
      "id": "fn1"
    }
  }, [_vm._v("1.")]), _vm._v(" Bruce K. Waltke and James M. Houston with Erika Moore, "), _c('i', [_vm._v("The Psalms as Christian Worship: A Historical Commentary")]), _vm._v(" (Grand Rapids/Cambridge, UK: Eerdmans, 2010), pp. 93-95.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('a', {
    staticClass: "fnnum",
    attrs: {
      "href": "#/eerpsalmschristian0006#fn2r",
      "id": "fn2"
    }
  }, [_vm._v("2.")]), _vm._v(" "), _c('i', [_vm._v("The Psalms as Christian Worship,")]), _vm._v(" pp. 446-83.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('a', {
    staticClass: "fnnum",
    attrs: {
      "href": "#/eerpsalmschristian0006#fn3r",
      "id": "fn3"
    }
  }, [_vm._v("3.")]), _vm._v(" "), _c('i', [_vm._v("The Psalms as Christian Worship,")]), _vm._v(" pp. 95-97.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('a', {
    staticClass: "fnnum",
    attrs: {
      "href": "#/eerpsalmschristian0006#fn4r",
      "id": "fn4"
    }
  }, [_vm._v("4.")]), _vm._v(" Francis Bacon, “Essays or Counsels Civil and Moral,” Essay V, in "), _c('i', [_vm._v("Harvard Classics,")]), _vm._v(" vol. 3, ed. Charles W. Eliot (New York: P. F. Collier & Son, 1937), p. 16.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('a', {
    staticClass: "fnnum",
    attrs: {
      "href": "#/eerpsalmschristian0006#fn5r",
      "id": "fn5"
    }
  }, [_vm._v("5.")]), _vm._v(" R. W. L. Moberly, "), _c('i', [_vm._v("Old Testament Theology: Reading the Hebrew Bible as Christian Scripture")]), _vm._v(" (Grand Rapids: Baker Academic, 2013), p. 236.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('a', {
    staticClass: "fnnum",
    attrs: {
      "href": "#/eerpsalmschristian0006#fn6r",
      "id": "fn6"
    }
  }, [_vm._v("6.")]), _vm._v(" Steven J. L. Croft, "), _c('i', [_vm._v("The Individual in the Psalms")]), _vm._v(" (Sheffield: JSOT Press, 1987), pp. 49-72.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('a', {
    staticClass: "fnnum",
    attrs: {
      "href": "#/eerpsalmschristian0006#fn7r",
      "id": "fn7"
    }
  }, [_vm._v("7.")]), _vm._v(" "), _c('i', [_vm._v("The Psalms as Christian Worship,")]), _vm._v(" pp. 91, 106.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('a', {
    staticClass: "fnnum",
    attrs: {
      "href": "#/eerpsalmschristian0006#fn8r",
      "id": "fn8"
    }
  }, [_vm._v("8.")]), _vm._v(" Samuel Terrien, "), _c('i', [_vm._v("The Psalms and Their Meaning for Today")]), _vm._v(" (Indianapolis: Bobbs-Merrill, 1952), p. viii.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('a', {
    staticClass: "fnnum",
    attrs: {
      "href": "#/eerpsalmschristian0006#fn9r",
      "id": "fn9"
    }
  }, [_vm._v("9.")]), _vm._v(" Robert W. Jenson, “For Us . . . He Was Made Man,” in "), _c('i', [_vm._v("Nicene Christianity: The Future for a New Ecumenism,")]), _vm._v(" ed. Christopher R. Seitz (Grand Rapids: Brazos, 2001), pp. 75-83.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('a', {
    staticClass: "fnnum",
    attrs: {
      "href": "#/eerpsalmschristian0006#fn10r",
      "id": "fn10"
    }
  }, [_vm._v("10.")]), _vm._v(" Clare Costley King’oo, "), _c('i', [_vm._v("Miserere Mei: The Penitential Psalms in Late Medieval and Early Modern England")]), _vm._v(" (Notre Dame, IN: University of Notre Dame Press, 2012). While comprehensive as a literary study, it reveals the Catholic bias toward a “penitential culture” that is still interpreted as “Augustinian” in origin. It ignores the Carolingian reform as being the strong precursor of medieval penitential culture.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('a', {
    staticClass: "fnnum",
    attrs: {
      "href": "#/eerpsalmschristian0006#fn11r",
      "id": "fn11"
    }
  }, [_vm._v("11.")]), _vm._v(" Michael P. Kuczynski, “The Psalms and Social Action in Late Medieval England,” in "), _c('i', [_vm._v("The Place of the Psalms in the Intellectual Culture of the Middle Ages,")]), _vm._v(" ed. Nancy van Dusen (Albany, NY: State University of New York Press, 1999), pp. 191-214.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('a', {
    staticClass: "fnnum",
    attrs: {
      "href": "#/eerpsalmschristian0006#fn12r",
      "id": "fn12"
    }
  }, [_vm._v("12.")]), _vm._v(" Clare Costley King’oo, “Rightful Penitence and the Publication of Wyatt’s "), _c('i', [_vm._v("Certayne Psalmes,")]), _vm._v("” in "), _c('i', [_vm._v("Psalms in the Early Modern World,")]), _vm._v(" ed. Linda Phyllis Austern, Kari Boyd McBride, and David L. Orvis (Burlington, VT: Ashgate, 2011), pp. 155-74.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('a', {
    staticClass: "fnnum",
    attrs: {
      "href": "#/eerpsalmschristian0006#fn13r",
      "id": "fn13"
    }
  }, [_vm._v("13.")]), _vm._v(" Hans S. Reinders, "), _c('i', [_vm._v("Receiving the Gift of Friendship: Profound Disability, Theological Anthropology, and Ethics")]), _vm._v(" (Grand Rapids /Cambridge, UK: Eerdmans, 2008).")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('a', {
    staticClass: "fnnum",
    attrs: {
      "href": "#/eerpsalmschristian0006#fn14r",
      "id": "fn14"
    }
  }, [_vm._v("14.")]), _vm._v(" David B. Burrell, Carlo Cogliati, Janet M. Soskice, and William R. Stoeger, eds., "), _c('i', [_vm._v("Creation and the God of Abraham")]), _vm._v(" (Cambridge: Cambridge University Press, 2010).")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('a', {
    staticClass: "fnnum",
    attrs: {
      "href": "#/eerpsalmschristian0006#fn15r",
      "id": "fn15"
    }
  }, [_vm._v("15.")]), _vm._v(" We can be sympathetic to attempts to relate the Psalms to human dependency and suffering, such as proposed by Dennis Sylva, "), _c('i', [_vm._v("Psalms and the Transformation of Stress: Poetic-Communal Interpretation and the Family")]), _vm._v(" (Louvain: Peeters, 1996), but such attempts do not interpret biblical lament.")]), _vm._v(" "), _c('p', {
    staticClass: "fn"
  }, [_c('a', {
    staticClass: "fnnum",
    attrs: {
      "href": "#/eerpsalmschristian0006#fn16r",
      "id": "fn16"
    }
  }, [_vm._v("16.")]), _vm._v(" See Bruce K. Waltke, “Biblical Theology of the Psalms Today: A Personal Perspective,” in "), _c('i', [_vm._v("The Psalms: Language for All Seasons of the Soul,")]), _vm._v(" ed. Andrew J. Schmutzer and David M. Howard Jr. (Chicago: Moody Publishers, 2013), pp. 19-28.")])])])])])
}]}


//////////////////
// WEBPACK FOOTER
// ../node_modules/vue-loader/lib/template-compiler?{"id":"data-v-597c560a","hasScoped":false,"transformToRequire":{"video":"src","source":"src","img":"src","image":"xlink:href"}}!../node_modules/vue-loader/lib/selector.js?type=template&index=0!./src/components/eer-psalmschristian-0006.vue
// module id = 69
// module chunks = 0